import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import {
  Music, Play, ArrowLeft, ExternalLink, Disc3,
} from "lucide-react";

/* /jukebox — Mike's Top 20 Christian Songs.
   Public page. Bright cream-and-gold "diner jukebox" feel — NO dark brown.
   Every card has a real visual (album cover when provided by Mike, otherwise
   a colourful CSS vinyl-record graphic). Pulls song data from
   /api/jukebox/songs so Mike can edit titles, URLs, and cover-art links
   from the admin tab without redeploying. */

const G = "#2E9F3A";
const O = "#F39A1F";
const WINE = "#0F172A";
const TEAL = "#287F8E";
const RED = "#E33E3E";

const MIKE_PORTRAIT_URL = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/0puvbqpl_MICHAEL%20BOYD%20AUTHOR%20EVIDENCES%20OF%20GOD.png";
const JUKEBOX_URL = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/bk0zz11d_MIKE%27S%20TOP%2020%20CHRISTIAN%20SONGS.png";

/* Used to colour the placeholder vinyl record so no two adjacent cards
   feel identical. Bright, jukebox-friendly hues. No browns. */
const VINYL_PALETTE = [
  ["#E63946", "#FFB54D"], // red → amber
  ["#287F8E", "#7ED4FF"], // teal → sky
  ["#2E9F3A", "#FFD166"], // green → gold
  ["#F39A1F", "#FFEAB3"], // gold → cream
  ["#7C3AED", "#F6C9FF"], // violet → pink
  ["#E33E3E", "#FFC1B6"], // crimson → blush
];

export default function JukeboxPage({ embedded = false }) {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    api.get("/jukebox/songs")
      .then((r) => setSongs(r.data?.songs || []))
      .catch(() => setSongs([]))
      .finally(() => setLoading(false));
  }, []);

  const goBack = () => {
    if (window.history.length > 1 && loc.key !== "default") return nav(-1);
    nav("/");
  };

  return (
    <div className="min-h-screen" data-testid="jukebox-page"
         style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #D9EEFB 50%, #EAF6FC 100%)" }}>
      {!embedded && <SiteNav />}

      {/* Floating back — bright pill */}
      {!embedded && (<button onClick={goBack} data-testid="jukebox-back"
              className="fixed top-20 left-3 md:left-6 z-30 inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs uppercase tracking-[0.22em] font-bold shadow-md hover:scale-[1.04] transition"
              style={{ background: "#FFFFFF", color: WINE, border: `2px solid ${O}` }}>
        <ArrowLeft className="w-4 h-4" /> Back
      </button>)}

      {/* ═══════════ HERO — Mike + Jukebox + Title ═══════════ */}
      <section className="relative overflow-hidden">
        <div className="relative max-w-6xl mx-auto px-5 md:px-10 py-10 md:py-16">
          <div className="text-center mb-8 md:mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] tracking-[0.32em] uppercase font-bold mb-4 text-white"
                 style={{ background: RED }}>
              <Disc3 className="w-3.5 h-3.5" /> ★ MIKE'S JUKEBOX ★
            </div>
            <h1 className="font-serif-display text-5xl sm:text-6xl md:text-7xl uppercase leading-[0.92]"
                style={{ color: WINE, textWrap: "balance" }}>
              Mike's Top 20<br/>
              <span style={{ color: RED, fontSize: "0.7em" }}>Christian Songs</span>
            </h1>
            <div className="mt-5 inline-block px-6 py-2 font-serif-display text-2xl md:text-3xl uppercase italic"
                 style={{
                   color: WINE,
                   border: `2px solid ${O}`,
                   borderRadius: "999px",
                   background: "#FFFFFF",
                   letterSpacing: "0.08em",
                 }}>
              Check &apos;em out!
            </div>
          </div>

          {/* Photo + jukebox composition — sits on a WHITE card so no dark background */}
          <div className="relative max-w-4xl mx-auto rounded-3xl shadow-2xl"
               style={{ background: "#FFFFFF", border: `4px solid ${O}`, padding: "24px" }}
               data-testid="jukebox-hero-art">
            <div className="grid grid-cols-[1fr_1fr] gap-0 items-end relative">
              {/* MIKE */}
              <div className="relative z-10" style={{ transform: "translateX(20px) translateY(10px)" }}>
                <img src={MIKE_PORTRAIT_URL} alt="Mike Boyd"
                     loading="lazy"
                     className="w-full h-auto block"
                     style={{
                       filter: "drop-shadow(0 12px 24px rgba(91,31,31,0.2))",
                       maxWidth: 380,
                     }}/>
              </div>
              {/* JUKEBOX */}
              <div className="relative" style={{ transform: "translateX(-20px)" }}>
                <img src={JUKEBOX_URL} alt="Jukebox"
                     loading="lazy"
                     className="w-full h-auto block"
                     style={{
                       filter: "drop-shadow(0 12px 24px rgba(227,62,62,0.25))",
                       maxWidth: 360,
                     }}/>
              </div>
            </div>
          </div>

          <p className="text-center mt-8 max-w-2xl mx-auto text-base md:text-lg italic" style={{ color: WINE }}>
            These are the 20 songs that wreck me, build me up, and keep my heart pointed
            heavenward. <strong>Tap any one to open it on Amazon Music.</strong>
          </p>
        </div>
      </section>

      {/* ═══════════ 20-SONG GRID ═══════════ */}
      <section className="relative pb-16 md:pb-24" data-testid="jukebox-songs">
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          {loading ? (
            <div className="text-center py-10" style={{ color: WINE }}>Loading the playlist…</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              {Array.from({ length: 20 }).map((_, i) => {
                const song = songs[i] || null;
                return <SongCard key={i} number={i + 1} song={song} />;
              })}
            </div>
          )}

          <div className="mt-10 text-center">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-3" style={{ color: O }}>
              ★ Affiliate Disclosure
            </div>
            <p className="text-xs italic max-w-xl mx-auto" style={{ color: WINE }}>
              Mike earns a small commission on songs purchased through these links at no extra cost to you.
              Thank you for supporting the ministry through your music.
            </p>
          </div>
        </div>
      </section>

      {!embedded && <SiteFooter />}
    </div>
  );
}

/* ─── Vinyl-record placeholder — bright CSS gradient, zero brown ─── */
function VinylRecord({ number }) {
  const [inner, outer] = VINYL_PALETTE[(number - 1) % VINYL_PALETTE.length];
  return (
    <div
      className="relative shrink-0 rounded-full overflow-hidden flex items-center justify-center"
      style={{
        width: 72,
        height: 72,
        background: `repeating-radial-gradient(circle at center, ${outer} 0 1.5px, ${inner} 1.5px 4px)`,
        boxShadow: `0 4px 12px rgba(91,31,31,0.18)`,
      }}
      aria-hidden="true"
    >
      {/* Center label */}
      <div
        className="rounded-full flex items-center justify-center font-serif-display font-bold"
        style={{
          width: 32,
          height: 32,
          background: "#FFFFFF",
          color: WINE,
          fontSize: 14,
          boxShadow: `0 0 0 2px ${outer}`,
        }}
      >
        {String(number).padStart(2, "0")}
      </div>
    </div>
  );
}

/* ─── Song card — bright, every slot gets a real visual ─── */
function SongCard({ number, song }) {
  const isReady = song && (song.amazon_url || song.youtube_id) && song.title;
  const hasCover = isReady && song.cover_url;
  const ytId = (song && song.youtube_id) || "";
  const [playing, setPlaying] = useState(false);

  // EMPTY/PLACEHOLDER state — bright, with vinyl record, never "no images"
  if (!isReady) {
    return (
      <div
        className="rounded-2xl p-4 flex items-center gap-4 transition bg-white"
        style={{ border: `2px solid ${O}33` }}
        data-testid={`song-card-${number}`}
      >
        <VinylRecord number={number} />
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm" style={{ color: WINE }}>Track {String(number).padStart(2, "0")}</div>
          <div className="text-xs italic" style={{ color: "#7A6543" }}>Mike&apos;s adding this song soon.</div>
        </div>
        <Music className="w-5 h-5 shrink-0" style={{ color: O }} />
      </div>
    );
  }

  return (
    <div data-testid={`song-card-${number}`}
         className="rounded-2xl bg-white overflow-hidden shadow"
         style={{ border: `2px solid ${O}55`,
                  boxShadow: "0 4px 14px rgba(91,31,31,0.08)" }}>
      <div className="p-4 flex items-center gap-4">
        {/* Album cover OR vinyl record */}
        {hasCover ? (
          <div className="shrink-0 rounded-xl overflow-hidden"
               style={{ width: 72, height: 72, border: `2px solid ${O}55` }}>
            <img src={song.cover_url} alt={`${song.title} album art`}
                 loading="lazy" className="block w-full h-full object-cover" />
          </div>
        ) : (
          <VinylRecord number={number} />
        )}

        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.22em] uppercase font-bold mb-0.5" style={{ color: O }}>
            ★ Track {String(number).padStart(2, "0")}
          </div>
          <div className="font-serif-display text-lg leading-tight truncate" style={{ color: WINE }}>
            {song.title}
          </div>
          <div className="text-xs italic truncate" style={{ color: TEAL }}>
            {song.artist}
          </div>
        </div>

        <div className="shrink-0 flex flex-col gap-1.5">
          {ytId && (
            <button onClick={() => setPlaying((p) => !p)}
                    data-testid={`song-play-${number}`}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[10px] uppercase tracking-[0.18em] font-bold text-white transition hover:scale-[1.05] whitespace-nowrap"
                    style={{ background: playing ? WINE : G }}>
              <Play className="w-3 h-3 fill-current" /> {playing ? "Close" : "Play"}
            </button>
          )}
          {song.amazon_url && (
            <a href={song.amazon_url}
               target="_blank"
               rel="noopener noreferrer sponsored"
               data-testid={`song-buy-${number}`}
               className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[10px] uppercase tracking-[0.18em] font-bold transition hover:scale-[1.05] whitespace-nowrap"
               style={{ background: "#FFFFFF", color: WINE, border: `1.5px solid ${WINE}` }}>
              Buy <ExternalLink className="w-3 h-3 opacity-80" />
            </a>
          )}
        </div>
      </div>

      {/* Inline YouTube embed — appears below the card row when user clicks Play.
          Auto-plays once expanded so seniors don't have to hunt for a button. */}
      {playing && ytId && (
        <div className="border-t-2" style={{ borderColor: `${O}33` }}
             data-testid={`song-player-${number}`}>
          <div className="relative w-full" style={{ aspectRatio: "16/9", background: "#000" }}>
            <iframe
              title={`${song.title} — ${song.artist}`}
              src={`https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0&modestbranding=1`}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </div>
  );
}
